import { Avatar, Box, Button, Footer, Layer, Text } from 'grommet';
import useYoutubeConnection, { AuthStatus } from './useYoutubeConnection';
import { useEffect, useState } from 'react';
import { LinkPrevious, Youtube } from 'grommet-icons';
import { truncateText } from './utils';
import { LiveStreamSettingsModal } from './LiveStreamSettingsModal';
import SHDButton from '@ui/Button/SHDButton';
import SHDText from '@ui/Text/SHDText';
import HowToAndErrorModal from './HowToAndErrorStreamingModal';
import { CustomCard } from 'src/features/StreamingConfig/FacebookDestinationPickerV3';
import {
  YOUTUBE_HOW_TO_SET_UP_STREAMING,
  YOUTUBE_STREAMING_ACCESS_ERROR,
  YOUTUBE_STREAMING_ACCESS_ERROR_SUBTITLE,
  YOUTUBE_STREAMING_ACCESS_ERROR_TITLE,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR_SUBTITLE,
  YOUTUBE_STREAMING_NO_CHANNEL_ERROR_TITLE,
} from '@constants/streamingHelpTexts';
import { analytics } from '@services/analytics';

interface YoutubeLinkModalProps {
  status: AuthStatus;
  onClose: () => void;
}
const YoutubeLinkModal: React.FC<YoutubeLinkModalProps> = ({
  status,
  onClose,
}) => {
  const title = status.channel
    ? 'Stream to YouTube'
    : 'Your account was not linked';

  const getSubtitle = () => {
    let subtitle;
    if (status.channel) {
      subtitle = `Success!`;
    } else if (status.error) {
      subtitle = status.error.message.title;
    }
    return subtitle;
  };

  const getDescription = () => {
    let description;
    if (status.channel) {
      description = 'You have successfully connected your YouTube channel.';
    }
    return description;
  };

  return (
    <Layer onClickOutside={onClose}>
      <Box direction="row" align={'center'} justify={'between'} pad={'large'}>
        <Box alignSelf="start" onClick={onClose}>
          <LinkPrevious size="24px" />
        </Box>
        <Text weight="bold" alignSelf="center">
          {title}
        </Text>
        <Box alignSelf="end" width={'24px'} />
      </Box>
      <Box pad="medium" gap="small">
        <Text weight="bold">{getSubtitle()}</Text>
        <Text>{getDescription()}</Text>
        {status.channel && (
          <Box gap="medium">
            <Box direction="row" align="center" gap="medium">
              <Avatar
                src={status.channel.channelInfo.snippet.thumbnails.default.url}
              />
              <Text weight={'bold'}>
                {status.channel.channelInfo.snippet.title}
              </Text>
            </Box>
            <Text>You can now livestream to this channel</Text>
          </Box>
        )}
        {status.error && (
          <Box>
            <Button
              secondary
              label="Help"
              href={status.error.link}
              target="_blank"
            />
          </Box>
        )}
      </Box>
      <Footer background={'light-1'}>
        <Box pad="medium" fill>
          <Button
            label={
              <Text weight="bold">{status.channel ? 'Finish' : 'Back'}</Text>
            }
            onClick={onClose}
          />
        </Box>
      </Footer>
    </Layer>
  );
};

interface Props {
  teamId: string;
  isAdmin: boolean;
  showUnlink?: boolean;
  truncateLength?: null | number;
  newLinkingFlow: boolean;
}
const YoutubeButton: React.FC<Props> = ({
  teamId,
  isAdmin,
  showUnlink = false,
  truncateLength = null,
  newLinkingFlow,
}) => {
  const [showModal, setShowModal] = useState(false);

  const { signIn, cancelSignin, status } = useYoutubeConnection({ teamId });

  useEffect(() => {
    if (!status) {
      return;
    }

    const { phase, error } = status;

    if (phase === 'completed') {
      if (newLinkingFlow) {
        setShowSuccessModal(true);
      } else {
        setShowModal(true);
      }
    } else if (newLinkingFlow) {
      if (error) {
        setShowErrorLayer(true);
      }
    }
  }, [status, newLinkingFlow]);

  const formatTeamName = (name: string) => {
    return truncateLength ? truncateText(name, truncateLength) : name;
  };

  const [showIntroModal, setShowIntroModal] = useState<boolean>(false);
  const [showHowToSetUpModal, setShowHowToSetUpModal] =
    useState<boolean>(false);
  const [showErrorLayer, setShowErrorLayer] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  const handleYTButtonClicked = () => {
    if (status?.phase === 'in-progress') {
      cancelSignin();
    } else if (newLinkingFlow) {
      setShowIntroModal(true);
    } else {
      signIn();
    }
  };

  return (
    <Box skeleton={!status}>
      <Box direction="row" justify="between" align="center">
        {status?.channel ? (
          <a
            href={`https://www.youtube.com/channel/${status.channel.credsChannelId}`}
            target="_blank"
            rel="noreferrer"
          >
            <Button
              primary
              label={
                <Text size="small" weight={'bold'}>
                  {formatTeamName(status?.channel.credsChannelName)}
                </Text>
              }
              color="#FF0000"
              icon={<Youtube color="white" size="small" />}
            />
          </a>
        ) : (
          isAdmin && (
            <Button
              secondary
              label={
                <Text color="#FF0000" size="small" weight={'bold'}>
                  {status?.phase === 'in-progress'
                    ? 'Cancel connect'
                    : 'Connect YouTube'}
                </Text>
              }
              color="#FF0000"
              icon={<Youtube color="#FF0000" size="small" />}
              onClick={handleYTButtonClicked}
            />
          )
        )}
        {showUnlink && status?.channel && (
          <Button secondary size="small" label="Unlink" onClick={() => {}} />
        )}
      </Box>
      {showIntroModal &&
        (newLinkingFlow ? (
          <LiveStreamSettingsModal
            headerText="LINK YOUTUBE"
            title="Have you ever livestreamed on YouTube?"
            onCloseModal={() => setShowIntroModal(false)}
          >
            <SHDButton
              secondary
              label={
                <SHDText size="button" style="subhead-bold" textAlign="center">
                  Yes, I have YouTube Studio set up
                </SHDText>
              }
              onClick={() => {
                analytics.track('Destination Path Choice Selected', {
                  pathSelected: 'Yes',
                  destinationType: 'YouTube',
                  teamId,
                });
                setShowIntroModal(false);
                signIn();
              }}
            />
            <SHDButton
              secondary
              label={
                <SHDText size="button" style="subhead-bold" textAlign="center">
                  No, I need help setting up
                </SHDText>
              }
              onClick={() => {
                setShowHowToSetUpModal(true),
                  analytics.track('Destination Path Choice Selected', {
                    pathSelected: 'No',
                    destinationType: 'YouTube',
                    teamId,
                  });
              }}
            />
          </LiveStreamSettingsModal>
        ) : null)}
      {showHowToSetUpModal &&
        (newLinkingFlow ? (
          <HowToAndErrorModal
            headerText="LINK YOUTUBE"
            title="How to set up YouTube livestreaming"
            subtitle=""
            onClose={() => setShowHowToSetUpModal(false)}
            isError={false}
            htmlContent={YOUTUBE_HOW_TO_SET_UP_STREAMING}
          />
        ) : null)}
      {showErrorLayer && status && status.error && (
        <HowToAndErrorModal
          headerText="LINK YOUTUBE"
          title={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR_TITLE
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR_TITLE
          }
          subtitle={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR_SUBTITLE
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR_SUBTITLE
          }
          onClose={() => setShowErrorLayer(false)}
          isError={true}
          htmlContent={
            status.error.enum === 'STREAMING_ACCESS'
              ? YOUTUBE_STREAMING_ACCESS_ERROR
              : YOUTUBE_STREAMING_NO_CHANNEL_ERROR
          }
        />
      )}
      {showSuccessModal && status?.channel && (
        <LiveStreamSettingsModal
          headerText="LINK YOUTUBE"
          title={`You can now stream to ${status.channel.channelInfo.snippet.title}`}
          onCloseModal={() => setShowSuccessModal(false)}
        >
          <Box style={{ marginTop: -20 }} gap="large" fill="vertical">
            <SHDText style="footnote">
              Sideline HD will now stream to the following channel
            </SHDText>
            <Box gap="large" flex="grow" overflow="auto">
              <CustomCard
                onClick={() => {}}
                name={status.channel.channelInfo.snippet.title}
                subtitle=""
                disabled={false}
                pictureUrl={
                  status.channel.channelInfo.snippet.thumbnails.default.url
                }
              />
            </Box>
            <SHDButton
              primary
              label="Done"
              onClick={() => setShowSuccessModal(false)}
            />
          </Box>
        </LiveStreamSettingsModal>
      )}
      {showModal && status && (
        <YoutubeLinkModal
          status={status}
          onClose={() => {
            setShowModal(false);
          }}
        />
      )}
    </Box>
  );
};

export default YoutubeButton;
